import { useScroll } from '@use-gesture/react';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import * as _ from 'lodash-es';
import type Quill from 'quill/core';
import type { Delta } from 'quill/core';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import { useEffectEvent } from 'use-effect-event';
import PageLayout from '../../app/layout/PageLayout';
import { useRightPanel } from '../../app/layout/RightPanel';
import Button from '../../components/Button';
import Caret from '../../components/Caret';
import OverflowMenu from '../../components/OverflowMenu';
import SVGIcon from '../../components/SVGIcon';
import VersionChangeFormat from '../../editor/formats/VersionChange';
import { useQuill } from '../../editor/QuillContext';
import { useScrollingToAnchorOnLoad } from '../../editor/useAnchorScrollingHandler';
import { getHeaderCollapse } from '../../editor/useHeaderCollapse';
import { getListCollapse } from '../../editor/useListCollapse';
import overflowMenuIcon from '../../icons/non-prefilled/16x16/overflowMenu.svg';
import { isFeatureEnabled } from '../../utils/features';
import PageMeta from '../../utils/PageMeta';
import { useIsSize } from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';
import { getScrollContainer } from '../listenToPostScroll';
import PostLayout from '../PostLayout';
import usePostChromeVisible from '../usePostChromeVisible';
import type { VersionChange } from './calculateVersionChanges';
import HistoricalPost from './HistoricalPost';
import * as styles from './VersionChanges.module.scss';

type VersionChangesContextValue = {
  isInitialized: boolean;
  updateVersionChanges: (quill: Quill) => void;
  versionChanges: VersionChange[];
  focusedIndex: number;
  focusedId: string | null;
  focusById: (id: string) => void;
  goToNextChange: () => void;
  goToPreviousChange: () => void;
};

const VersionChangesContext =
  React.createContext<VersionChangesContextValue | null>(null);

export const useVersionChangesContext = () => {
  const value = useContext(VersionChangesContext);
  if (!value) {
    throw new Error(
      'useVersionChangesContext must be used within a VersionChangesContext.Provider',
    );
  }
  return value;
};

interface WrapperProps {
  postTitle?: string;
  children?: React.ReactNode;
  initialTitle: string;
}

const Wrapper = ({ postTitle, children, initialTitle }: WrapperProps) => {
  const { isRightPanelOpen } = useRightPanel();

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.isRightPanelOpen]: isRightPanelOpen,
      })}
    >
      <PageMeta
        title={postTitle ? `${postTitle} - ${initialTitle}` : initialTitle}
      />
      {children}
    </div>
  );
};

interface MetaProps {
  children: React.ReactNode;
}

const Meta = ({ children }: MetaProps) => {
  return <div className={styles.versionMeta}>{children}</div>;
};

interface TopbarContainerProps {
  children?: React.ReactNode;
}

const TopbarContainer = ({ children }: TopbarContainerProps) => {
  const isMobile = useIsSize(Sizes.SM);
  const isChromeVisible = usePostChromeVisible({ enabled: isMobile });

  return (
    <div
      className={classNames(styles.barContainer, {
        [styles.hide]: !isChromeVisible,
      })}
    >
      {children}
    </div>
  );
};

interface ChangesWallContainerProps {
  children?: React.ReactNode;
}

const ChangesWallContainer = ({ children }: ChangesWallContainerProps) => {
  return <div className={styles.changes}>{children}</div>;
};

interface TopbarProps {
  children?: React.ReactNode;
  overflowMenu?: React.ReactNode;
}

const Topbar = ({ children, overflowMenu }: TopbarProps) => {
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const {
    isInitialized,
    versionChanges,
    focusedIndex,
    goToNextChange,
    goToPreviousChange,
  } = useVersionChangesContext();

  return (
    <div className={styles.bar}>
      <div className={styles.barContent}>
        {children}

        {!!overflowMenu && (
          <OverflowMenu
            placement="bottom-start"
            open={isOverflowMenuOpen}
            onOpenChange={setIsOverflowMenuOpen}
            button={
              <Button
                className={classNames(styles.barButton, styles.muted, {
                  [styles.active]: isOverflowMenuOpen,
                })}
                aria-label="More options"
              >
                <SVGIcon>{overflowMenuIcon}</SVGIcon>
              </Button>
            }
          >
            {overflowMenu}
          </OverflowMenu>
        )}
      </div>
      <div className={styles.cyclingContainer}>
        {isInitialized && (
          <div className={styles.cyclingLabel}>
            {versionChanges.length > 0 ? (
              <>
                {focusedIndex + 1} / {versionChanges.length} changes
              </>
            ) : (
              'No changes'
            )}
          </div>
        )}

        <div
          className={classNames(styles.cyclingButtons, {
            [styles.showDivider]: !isFeatureEnabled('history_v2'),
          })}
        >
          <Button
            aria-label="Previous Change"
            disabled={focusedIndex <= 0}
            className={styles.barButton}
            onClick={goToPreviousChange}
          >
            <Caret block size="regular" direction="up" />
          </Button>
          <Button
            aria-label="Next Change"
            disabled={focusedIndex >= versionChanges.length - 1}
            className={styles.barButton}
            onClick={goToNextChange}
          >
            <Caret block size="regular" direction="down" />
          </Button>
        </div>
      </div>
    </div>
  );
};

interface PostLayoutTopbarContainerProps {
  children?: React.ReactNode;
}

const PostLayoutTopbarContainer = ({
  children,
}: PostLayoutTopbarContainerProps) => {
  const isMobile = useIsSize(Sizes.SM);
  const isChromeVisible = usePostChromeVisible({ enabled: isMobile });

  return (
    <PostLayout.StateBar
      className={classNames(styles.barContainer, {
        [styles.hide]: !isChromeVisible,
      })}
    >
      {children}
    </PostLayout.StateBar>
  );
};

interface PostProps {
  content: Delta | undefined;
}

const Post = ({ content }: PostProps) => {
  const { quill } = useQuill();
  const { updateVersionChanges, focusedId } = useVersionChangesContext();

  useEffect(() => {
    if (quill && focusedId) {
      const listCollapseManager = getListCollapse(quill);
      const headerCollapseManager = getHeaderCollapse(quill);

      const element = quill.root.querySelector(
        `[${VersionChangeFormat.keyName}="${focusedId}"]`,
      );
      const blot = quill.scroll.find(element, true);
      if (!blot) return;

      listCollapseManager?.revealBlot(blot);
      headerCollapseManager?.revealBlot(blot);
    }
  }, [quill, focusedId]);

  if (!content) return null;

  return (
    <HistoricalPost
      content={content}
      onBlotComponentsInitialLoad={updateVersionChanges}
    />
  );
};

interface ContentGridProps {
  children: React.ReactNode;
}

const ContentGrid = ({ children }: ContentGridProps) => {
  return <div className={styles.contentGrid}>{children}</div>;
};

interface ContentContainerProps {
  children: React.ReactNode;
}

const ContentContainer = ({ children }: ContentContainerProps) => {
  return <div className={styles.contentContainer}>{children}</div>;
};

interface ScrollContainerProps {
  children: React.ReactNode;
}

const ScrollContainer = ({ children }: ScrollContainerProps) => {
  return (
    <PageLayout.Content
      className={styles.scrollContainer}
      noPaddingX
      noPaddingY
    >
      {children}
    </PageLayout.Content>
  );
};

interface ContentProps {
  children: React.ReactNode;
}

const Content = ({ children }: ContentProps) => (
  <div className={styles.content}>{children}</div>
);

const moveFocusedClass = (root: HTMLElement, id: string | null) => {
  const focusedElements = new Set<Element>();
  if (id) {
    root
      .querySelectorAll(`[${VersionChangeFormat.keyName}="${id}"]`)
      .forEach((element) => {
        focusedElements.add(element);
        element.classList.add('focused-diff');
      });
  }

  Array.from(root.getElementsByClassName('focused-diff')).forEach((element) => {
    if (!focusedElements.has(element)) {
      element.classList.remove('focused-diff');
    }
  });
};

const scrollVersionChangeIntoView = (root: HTMLElement, id: string) => {
  const element = root.querySelector<HTMLElement>(
    `[${VersionChangeFormat.keyName}="${id}"]`,
  );
  if (element) {
    scrollIntoView(element, { scrollMode: 'if-needed' });
  }
};

interface VersionChangeState {
  isInitialized: boolean;
  versionChanges: VersionChange[];
  focusedId: string | null;
}

type Action =
  | { type: 'focus'; id: string }
  | { type: 'setVersionChanges'; versionChanges: VersionChange[] };

const reducer = (
  state: VersionChangeState,
  action: Action,
): VersionChangeState => {
  switch (action.type) {
    case 'focus':
      return { ...state, focusedId: action.id };
    case 'setVersionChanges':
      return {
        ...state,
        isInitialized: true,
        versionChanges: action.versionChanges,
        focusedId: action.versionChanges.find((v) => v.id === state.focusedId)
          ? state.focusedId
          : (action.versionChanges[0]?.id ?? null),
      };
    default:
      return state;
  }
};

const initialState: VersionChangeState = {
  isInitialized: false,
  versionChanges: [],
  focusedId: null,
};

interface RootProps {
  children: React.ReactNode;
  allVersionChanges: VersionChange[] | undefined;
  onFocusChange?: (id: string) => void;
  hideFocusedDiff?: boolean;
}

const Root = ({
  children,
  allVersionChanges,
  onFocusChange = () => {},
  hideFocusedDiff,
}: RootProps) => {
  const onFocusChangeEvent = useEffectEvent(onFocusChange);
  const { quill } = useQuill();

  const [{ isInitialized, versionChanges, focusedId }, reducerDispatch] =
    useReducer(reducer, initialState);

  const focusById = useCallback((id: string) => {
    reducerDispatch({ type: 'focus', id });
    onFocusChangeEvent(id);
  }, []);

  const allVersionChangesRef = useRef<typeof allVersionChanges>();

  const updateVersionChanges = useCallback(
    ({ root }: Quill) => {
      if (!allVersionChanges) return;

      allVersionChangesRef.current = allVersionChanges;
      const elements = Array.from(
        root.querySelectorAll<HTMLElement>(`[${VersionChangeFormat.keyName}]`),
      );

      const availableIds = elements.map((element) =>
        element.getAttribute(VersionChangeFormat.keyName),
      );

      const newVersionChanges = allVersionChanges.filter((v) =>
        availableIds.includes(v.id),
      );

      reducerDispatch({
        type: 'setVersionChanges',
        versionChanges: newVersionChanges,
      });
    },
    [allVersionChanges],
  );

  useEffect(() => {
    if (
      quill &&
      allVersionChangesRef.current &&
      !_.isEqual(allVersionChanges, allVersionChangesRef.current)
    ) {
      updateVersionChanges(quill);
    }
  }, [quill, updateVersionChanges, allVersionChanges]);

  const focusedIndex = Math.max(
    versionChanges.findIndex((v) => v.id === focusedId),
    0,
  );

  useEffect(() => {
    if (!quill) return undefined;

    const listener = (event: MouseEvent) => {
      if (event.target instanceof Node) {
        const element =
          event.target instanceof Element
            ? event.target
            : event.target.parentElement;
        const id = element
          ?.closest(`[${VersionChangeFormat.keyName}]`)
          ?.getAttribute(VersionChangeFormat.keyName);
        if (id) {
          focusById(id);
        }
      }
    };
    quill.root.addEventListener('click', listener);
    return () => {
      quill.root.removeEventListener('click', listener);
    };
  }, [quill, versionChanges, focusById]);

  useEffect(() => {
    if (!quill) return;
    if (hideFocusedDiff) {
      moveFocusedClass(quill.root, null);
    } else {
      moveFocusedClass(quill.root, focusedId);
    }
  }, [quill, focusedId, hideFocusedDiff]);

  const prevFocusedId = useRef<string | null>(null);

  useEffect(() => {
    if (!quill || !focusedId) return;

    const isInitialCall = prevFocusedId.current == null;
    prevFocusedId.current = focusedId;

    if (document.location.hash && isInitialCall) {
      // if we have a hash in the URL, useScrollingToAnchorOnLoad will handle the initial scroll
      return;
    }

    scrollVersionChangeIntoView(quill.root, focusedId);
  }, [quill, focusedId]);

  useScrollingToAnchorOnLoad(quill);

  const value = useMemo<VersionChangesContextValue>(
    () => ({
      isInitialized,
      focusById,
      goToNextChange() {
        const newId = versionChanges[focusedIndex + 1]?.id;
        if (!newId) return;
        focusById(newId);
        if (quill) {
          scrollVersionChangeIntoView(quill.root, newId);
        }
      },
      goToPreviousChange() {
        const newId = versionChanges[focusedIndex - 1]?.id;
        if (!newId) return;
        if (quill) {
          scrollVersionChangeIntoView(quill.root, newId);
          focusById(newId);
        }
      },
      updateVersionChanges,
      versionChanges,
      focusedIndex,
      focusedId,
    }),
    [
      focusedIndex,
      focusedId,
      versionChanges,
      quill,
      updateVersionChanges,
      isInitialized,
      focusById,
    ],
  );

  return (
    <VersionChangesContext.Provider value={value}>
      {children}
    </VersionChangesContext.Provider>
  );
};

const FOOTER_ANIMATION_TRANSITION = {
  duration: 0.2,
};

const STICKY_FOOTER_THRESHOLD = 500;

interface FooterProps {
  children: React.ReactNode;
  header: React.ReactNode;
}

const Footer = ({ children, header }: FooterProps) => {
  const { versionChanges } = useVersionChangesContext();
  const [isHovered, setIsHovered] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const isChromeVisible = usePostChromeVisible({ enabled: isSticky });
  const waypointRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollTargetRef = useRef<EventTarget | null>(null);

  useLayoutEffect(() => {
    const scrollContainer = getScrollContainer();
    if (!scrollContainer || isEnabled) return undefined;

    scrollTargetRef.current =
      scrollContainer === document.documentElement ? window : scrollContainer;
    const footerHeight = footerRef.current?.clientHeight ?? 0;

    const observer = new ResizeObserver(() => {
      const { clientHeight, scrollHeight } = scrollContainer;

      if (
        scrollHeight - footerHeight >
        clientHeight + STICKY_FOOTER_THRESHOLD
      ) {
        setIsEnabled(true);
        setIsSticky(true);
      }
    });

    observer.observe(scrollContainer);

    return () => {
      observer.disconnect();
    };
  }, [isEnabled, versionChanges]);

  useScroll(
    ({ values: [, scrollTop], target }) => {
      const clientHeight =
        target instanceof HTMLElement
          ? target.clientHeight
          : window.innerHeight;

      const waypointTop = waypointRef.current?.offsetTop ?? 0;
      const scrollBottom = scrollTop + clientHeight;
      setIsSticky(scrollBottom < waypointTop);
    },
    { target: scrollTargetRef, enabled: isEnabled },
  );

  const isExpanded = !isSticky || isHovered || isChromeVisible;

  return (
    <>
      <div className={styles.footerWaypoint} ref={waypointRef} />
      <motion.div
        animate={{
          bottom: isExpanded ? 0 : -(contentRef.current?.clientHeight ?? 0),
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={classNames(styles.footerContainer, {
          [styles.isSticky]: isSticky,
        })}
        transition={FOOTER_ANIMATION_TRANSITION}
      >
        <motion.div
          initial={false}
          animate={{
            paddingTop: isExpanded ? 26 : 16,
            paddingBottom: isExpanded ? 26 : 0,
          }}
          className={styles.footer}
          ref={footerRef}
          transition={FOOTER_ANIMATION_TRANSITION}
        >
          <motion.div
            initial={false}
            style={{ paddingBottom: isExpanded ? 20 : 16 }}
            transition={FOOTER_ANIMATION_TRANSITION}
          >
            {header}
          </motion.div>
          <div ref={contentRef}>{children}</div>
        </motion.div>
      </motion.div>
    </>
  );
};

export default {
  Root,
  Content,
  ContentContainer,
  ContentGrid,
  ChangesWallContainer,
  Footer,
  Topbar,
  PostLayoutTopbarContainer,
  TopbarContainer,
  Post,
  Wrapper,
  Meta,
  ScrollContainer,
};
